import API from "../global/api";
import {
  uploadFileInChunks,
  uploadErrorMessage,
  isUploadCancelled,
} from "../global/chunkedUpload";

// FBR Active Taxpayer List (ATL): upload the daily workbook, follow its import,
// and browse / search the list that is live.

// Getting ATL List (browse / search)
export const getAtlListRequest = () => {
  return {
    type: "ATL_LIST_REQUEST",
  };
};
export const getAtlListSuccess = (data) => {
  return {
    type: "ATL_LIST_SUCCESS",
    payload: data,
  };
};
export const getAtlListError = (msg) => {
  return {
    type: "ATL_LIST_ERROR",
    payload: msg,
  };
};

// A name search can take a moment while an ID search returns at once, so
// answers do not always come back in the order they were asked. Only the
// newest request may fill the table.
let latestListRequest = 0;

export const getAtlList = (page, limit, search) => {
  return (dispatch) => {
    const request = ++latestListRequest;
    dispatch(getAtlListRequest());
    return API.get(
      `/atl/search?page=${page}&limit=${limit}&search=${encodeURIComponent(search || "")}`,
    )
      .then((res) => {
        if (request !== latestListRequest) return;
        dispatch(getAtlListSuccess(res.data));
      })
      .catch((error) => {
        if (request !== latestListRequest) return;
        dispatch(
          getAtlListError(
            error?.response?.data?.message ||
              "Could not load the taxpayer list. Please try again.",
          ),
        );
      });
  };
};

// Getting ATL Import Status (running upload/import + what is live)
export const getAtlStatusRequest = () => {
  return {
    type: "ATL_STATUS_REQUEST",
  };
};
export const getAtlStatusSuccess = (data) => {
  return {
    type: "ATL_STATUS_SUCCESS",
    payload: data,
  };
};
export const getAtlStatusError = (msg) => {
  return {
    type: "ATL_STATUS_ERROR",
    payload: msg,
  };
};
export const getAtlStatus = () => {
  return (dispatch) => {
    dispatch(getAtlStatusRequest());
    return API.get(`/atl/import/status`)
      .then((res) => {
        dispatch(getAtlStatusSuccess(res.data));
        return res.data;
      })
      .catch((error) => {
        dispatch(
          getAtlStatusError(
            error?.response?.data?.message || "Could not read the import status.",
          ),
        );
        return null;
      });
  };
};

// Getting ATL Import History
export const getAtlHistoryRequest = () => {
  return {
    type: "ATL_HISTORY_REQUEST",
  };
};
export const getAtlHistorySuccess = (data) => {
  return {
    type: "ATL_HISTORY_SUCCESS",
    payload: data,
  };
};
export const getAtlHistoryError = (msg) => {
  return {
    type: "ATL_HISTORY_ERROR",
    payload: msg,
  };
};
export const getAtlHistory = (limit = 10) => {
  return (dispatch) => {
    dispatch(getAtlHistoryRequest());
    return API.get(`/atl/import/history?limit=${limit}`)
      .then((res) => {
        dispatch(getAtlHistorySuccess(res.data));
      })
      .catch((error) => {
        dispatch(
          getAtlHistoryError(
            error?.response?.data?.message || "Could not load the upload history.",
          ),
        );
      });
  };
};

// Cancelling an ATL Upload that is still in progress
export const cancelAtlUploadRequest = () => {
  return {
    type: "CANCEL_ATL_UPLOAD_REQUEST",
  };
};
export const cancelAtlUploadSuccess = (data) => {
  return {
    type: "CANCEL_ATL_UPLOAD_SUCCESS",
    payload: data,
  };
};
export const cancelAtlUploadError = (msg) => {
  return {
    type: "CANCEL_ATL_UPLOAD_ERROR",
    payload: msg,
  };
};
export const cancelAtlUpload = (uploadId, handleVisible, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(cancelAtlUploadRequest());
    return API.post(`/atl/upload/${uploadId}/cancel`)
      .then((res) => {
        dispatch(cancelAtlUploadSuccess(res.data));
        handleVisible?.(res.data.message);
        dispatch(getAtlStatus());
        dispatch(getAtlHistory());
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || "Could not cancel the upload.";
        dispatch(cancelAtlUploadError(message));
        handleDangerVisible?.(message);
        dispatch(getAtlStatus());
      });
  };
};

// Uploading the ATL File
//
// The upload runs for minutes and must not stop because the admin opened
// another screen, so it lives here rather than in a component: the loop keeps
// going whatever is on screen, and any screen can read its progress from the
// store. Only one can run at a time.
export const uploadAtlFileRequest = (data) => {
  return {
    type: "UPLOAD_ATL_FILE_REQUEST",
    payload: data,
  };
};
export const uploadAtlFileProgress = (data) => {
  return {
    type: "UPLOAD_ATL_FILE_PROGRESS",
    payload: data,
  };
};
export const uploadAtlFileSuccess = (data) => {
  return {
    type: "UPLOAD_ATL_FILE_SUCCESS",
    payload: data,
  };
};
export const uploadAtlFileError = (msg) => {
  return {
    type: "UPLOAD_ATL_FILE_ERROR",
    payload: msg,
  };
};
export const uploadAtlFileCancelled = () => {
  return {
    type: "UPLOAD_ATL_FILE_CANCELLED",
  };
};

let uploadController = null;
// Progress arrives many times a second; the screen needs it a few times.
const PROGRESS_INTERVAL_MS = 250;

// Closing the tab while bytes are still being sent stops the upload, so the
// browser is asked to confirm. Once the server has the whole file the import
// carries on by itself and this is removed.
const warnBeforeLeaving = (event) => {
  event.preventDefault();
  event.returnValue = "";
};

export const uploadAtlFile = (file) => {
  return (dispatch) => {
    if (uploadController) return Promise.resolve();
    uploadController = new AbortController();
    const controller = uploadController;
    let lastProgress = 0;
    let uploadId = null;

    dispatch(uploadAtlFileRequest({ fileName: file.name, total: file.size }));
    window.addEventListener("beforeunload", warnBeforeLeaving);

    return uploadFileInChunks({
      file,
      signal: controller.signal,
      onProgress: (progress) => {
        if (progress.uploadId) uploadId = progress.uploadId;
        const now = Date.now();
        const finished = progress.loaded >= progress.total;
        if (!finished && !progress.resumed && now - lastProgress < PROGRESS_INTERVAL_MS) return;
        lastProgress = now;
        dispatch(uploadAtlFileProgress(progress));
      },
    })
      .then((result) =>
        // The status is fetched before the upload is marked finished, so the
        // store already shows the import running at that moment and the screen
        // never has an instant where nothing appears to be happening.
        dispatch(getAtlStatus()).then(() => {
          dispatch(uploadAtlFileSuccess(result));
        }),
      )
      .catch((error) => {
        if (!isUploadCancelled(error)) {
          dispatch(uploadAtlFileError(uploadErrorMessage(error)));
          return dispatch(getAtlStatus());
        }
        dispatch(uploadAtlFileCancelled());
        // Cancelled on purpose: free the server's upload slot and its copy.
        const released = uploadId
          ? API.post(`/atl/upload/${uploadId}/cancel`).catch(() => {})
          : Promise.resolve();
        return released.then(() => dispatch(getAtlStatus()));
      })
      .finally(() => {
        window.removeEventListener("beforeunload", warnBeforeLeaving);
        if (uploadController === controller) uploadController = null;
        dispatch(getAtlHistory());
      });
  };
};

export const stopAtlUpload = () => {
  return () => {
    if (uploadController) uploadController.abort();
  };
};

export const clearAtlUploadResult = () => {
  return {
    type: "UPLOAD_ATL_FILE_CLEAR",
  };
};
